import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Search,
  Filter,
  X,
  SlidersHorizontal,
  Star,
  Heart,
  ShoppingCart,
  Eye,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { API_BASE_URL } from '../api';

const BACKEND_URL = API_BASE_URL;
const API = `${BACKEND_URL}/api`;

const AdvancedSearch = ({ onResults, showFilters = true }) => {
  const { t, language, currency } = useLanguage();
  const isRTL = language === 'ar';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    material: searchParams.get('material') || '',
    rating: searchParams.get('rating') || '',
    inStock: searchParams.get('inStock') === 'true',
    onSale: searchParams.get('onSale') === 'true',
    sortBy: searchParams.get('sortBy') || 'relevance'
  });

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const getSuggestionLabel = (suggestion) => {
    if (typeof suggestion === 'string') return suggestion;
    const label = suggestion?.name_en || suggestion?.name || suggestion?.name_ar || suggestion?.title || '';
    return typeof label === 'string' ? label : String(label || '');
  };

  /*
    A panel headed «اقتراحات ذكية» — Smart Suggestions, under a lightning bolt
    — used to open here. Behind it were four sentences typed into this file:
    «قلادات ذهبية للمناسبات», «أقراط لؤلؤ كلاسيكية», «أساور فضية عصرية»,
    «ساعات فاخرة للنساء», narrowed by a branch its own author had labelled
    "Simple AI-like filtering".

    Nothing about it was smart and nothing about it was true. It offered
    classic pearl earrings and modern silver bracelets to a shopper of a
    catalogue that holds neither, and it did so in the shop's own voice rather
    than the supplier's — the same lie as «مرصّع بالألماس», one screen earlier
    in the journey. The real suggestion list below it comes from /api/search
    and contains products that exist.
  */

  // Debounced product suggestions, drawn from the catalogue.
  const debouncedSearch = useCallback(
    debounce(async (searchQuery) => {
      if (searchQuery.length < 2) {
        setSuggestions([]);
        return;
      }

      try {
        setIsSearching(true);
        
        // Fetch regular suggestions
        // /search, not /search/suggestions — the second was never written on
        // the server, so the box's suggestions answered 404 and silently showed
        // nothing. The real search endpoint returns matching products, which is
        // what a suggestion list is.
        const response = await axios.get(
          `${API}/search?q=${encodeURIComponent(searchQuery)}&limit=6`
        );
        const suggestionItems = Array.isArray(response.data)
          ? response.data
          : response.data?.products || [];
        setSuggestions(suggestionItems.map(getSuggestionLabel).filter(Boolean));
        
        setShowSuggestions(true);
      } catch (error) {
        console.error('Search suggestions error:', error);
        setSuggestions([]);
        setShowSuggestions(true);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    if (query) {
      debouncedSearch(query);
    } else {
      setShowSuggestions(false);
      setSuggestions([]);
    }
  }, [query, debouncedSearch]);

  const handleSearch = async (searchQuery = query, newFilters = filters) => {
    if (!searchQuery.trim() && !Object.values(newFilters).some(v => v)) return;

    try {
      setIsSearching(true);
      setShowSuggestions(false);

      // Update URL params
      const params = new URLSearchParams();
      if (searchQuery) params.set('q', searchQuery);
      Object.entries(newFilters).forEach(([key, value]) => {
        if (value && value !== '') {
          params.set(key, value);
        }
      });

      setSearchParams(params);

      // Perform search
      const response = await axios.get(`${API}/search?${params.toString()}`);
      const results = response.data || [];
      
      if (onResults) {
        onResults(results);
      } else {
        navigate(`/products?${params.toString()}`);
      }
    } catch (error) {
      console.error('Search error:', error);
      if (onResults) {
        onResults([]);
      }
    } finally {
      setIsSearching(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    // Auto-search when filters change
    if (query || Object.values(newFilters).some(v => v)) {
      handleSearch(query, newFilters);
    }
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      minPrice: '',
      maxPrice: '',
      material: '',
      rating: '',
      inStock: false,
      onSale: false,
      sortBy: 'relevance'
    });
    setQuery('');
    setSearchParams({});
    if (onResults) {
      onResults([]);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    if (typeof suggestion === 'string') {
      setQuery(suggestion);
      handleSearch(suggestion, filters);
    } else {
      const productName = getSuggestionLabel(suggestion);
      setQuery(productName);
      handleSearch(productName, filters);
    }
    setShowSuggestions(false);
  };

  return (
    <div className="relative" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Main Search Bar */}
      <div className="relative">
        <div className="relative flex items-center">
          <div className="relative flex-1">
            <Search className={`absolute ${isRTL ? 'right-4' : 'left-4'} top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400`} />
            <Input
              type="text"
              placeholder={isRTL ? 'ابحث عن المنتجات، العلامات التجارية، الفئات...' : 'Search for products, brands, categories...'}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className={`${isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-3 text-lg border-2 border-amber-200 focus:border-amber-500 rounded-full shadow-lg`}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className={`absolute ${isRTL ? 'left-4' : 'right-4'} top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600`}
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
          
          <Button
            onClick={() => handleSearch()}
            disabled={isSearching}
            className="ml-3 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 rounded-full shadow-lg"
          >
            {isSearching ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <Search className="h-5 w-5" />
            )}
          </Button>
          
          {showFilters && (
            <Button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              variant="outline"
              className="ml-2 px-4 py-3 rounded-full border-2 border-amber-200 hover:border-amber-500"
            >
              <SlidersHorizontal className="h-5 w-5" />
            </Button>
          )}
        </div>

        {/* Search Suggestions */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-80 overflow-y-auto">
            {suggestions.length > 0 && (
              <div className="p-3">
                <div className="flex items-center mb-2">
                  <Search className="h-4 w-4 text-gray-400 me-2" />
                  <span className="text-sm font-medium text-gray-700">
                    {isRTL ? 'اقتراحات البحث' : 'Search Suggestions'}
                  </span>
                </div>
                {suggestions.slice(0, 5).map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="w-full text-left p-2 hover:bg-gray-50 rounded-md transition-colors text-sm text-gray-700"
                  >
                    {getSuggestionLabel(suggestion)}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Advanced Filters */}
      {showFilters && showAdvancedFilters && (
        <div className="mt-4 p-6 bg-white rounded-lg shadow-lg border border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {isRTL ? 'فلاتر متقدمة' : 'Advanced Filters'}
            </h3>
            <Button onClick={clearFilters} variant="ghost" size="sm">
              <X className="h-4 w-4 me-1" />
              {isRTL ? 'مسح الكل' : 'Clear All'}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isRTL ? 'الفئة' : 'Category'}
              </label>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              >
                <option value="">{isRTL ? 'جميع الفئات' : 'All Categories'}</option>
                <option value="necklaces">{isRTL ? 'قلادات' : 'Necklaces'}</option>
                <option value="earrings">{isRTL ? 'أقراط' : 'Earrings'}</option>
                <option value="bracelets">{isRTL ? 'أساور' : 'Bracelets'}</option>
                <option value="rings">{isRTL ? 'خواتم' : 'Rings'}</option>
                <option value="watches">{isRTL ? 'ساعات' : 'Watches'}</option>
                <option value="sets">{isRTL ? 'أطقم' : 'Sets'}</option>
              </select>
            </div>

            {/* Price Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isRTL ? 'نطاق السعر' : 'Price Range'}
              </label>
              <div className="flex space-x-2">
                <Input
                  type="number"
                  placeholder={isRTL ? 'من' : 'Min'}
                  value={filters.minPrice}
                  onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                  className="w-1/2"
                />
                <Input
                  type="number"
                  placeholder={isRTL ? 'إلى' : 'Max'}
                  value={filters.maxPrice}
                  onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                  className="w-1/2"
                />
              </div>
            </div>

            {/* Material Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isRTL ? 'المادة' : 'Material'}
              </label>
              <select
                value={filters.material}
                onChange={(e) => handleFilterChange('material', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              >
                {/*
                  The words this catalogue actually states. "Platinum" and
                  "Diamond" were on the list and match nothing the shop sells,
                  while stainless steel — most of the men's range — was not
                  offered at all. The filter reads the material the product
                  states, so an option that matches nothing returns an empty
                  grid, and an empty grid for "Platinum" is a promise the shop
                  cannot keep.
                */}
                <option value="">{isRTL ? 'جميع الخامات' : 'All Materials'}</option>
                <option value="gold">{isRTL ? 'ذهب' : 'Gold'}</option>
                <option value="silver">{isRTL ? 'فضة' : 'Silver'}</option>
                <option value="steel">{isRTL ? 'ستيل' : 'Steel'}</option>
                <option value="zircon">{isRTL ? 'زركون' : 'Zircon'}</option>
              </select>
            </div>

            {/*
              A row of colour swatches used to sit here, and a size box beside
              it. No product in this shop carries a colour or a size — not one
              imported from the supplier, and no screen that can set one — so
              picking a swatch narrowed the results by a field that is empty on
              every row. The filter did nothing, which was the kinder of the
              two possible outcomes.
            */}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            {/* Rating Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isRTL ? 'التقييم' : 'Rating'}
              </label>
              <select
                value={filters.rating}
                onChange={(e) => handleFilterChange('rating', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              >
                <option value="">{isRTL ? 'جميع التقييمات' : 'All Ratings'}</option>
                <option value="4">{isRTL ? '4 نجوم وأكثر' : '4+ Stars'}</option>
                <option value="3">{isRTL ? '3 نجوم وأكثر' : '3+ Stars'}</option>
              </select>
            </div>

            {/* Stock Filter */}
            <div className="flex items-center">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.inStock}
                  onChange={(e) => handleFilterChange('inStock', e.target.checked)}
                  className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  {isRTL ? 'متوفر في المخزون' : 'In Stock Only'}
                </span>
              </label>
            </div>

            {/* Sale Filter */}
            <div className="flex items-center">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.onSale}
                  onChange={(e) => handleFilterChange('onSale', e.target.checked)}
                  className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  {isRTL ? 'عروض خاصة' : 'On Sale'}
                </span>
              </label>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isRTL ? 'ترتيب حسب' : 'Sort By'}
            </label>
            <select
              value={filters.sortBy}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              className="w-full md:w-auto px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            >
              <option value="relevance">{isRTL ? 'الأكثر صلة' : 'Most Relevant'}</option>
              <option value="price-low-high">{isRTL ? 'السعر: من الأقل إلى الأعلى' : 'Price: Low to High'}</option>
              <option value="price-high-low">{isRTL ? 'السعر: من الأعلى إلى الأقل' : 'Price: High to Low'}</option>
              <option value="rating">{isRTL ? 'الأعلى تقييماً' : 'Highest Rated'}</option>
              <option value="newest">{isRTL ? 'الأحدث' : 'Newest'}</option>
              <option value="popular">{isRTL ? 'الأكثر شيوعاً' : 'Most Popular'}</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default AdvancedSearch;